"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.DmsCarousel = void 0;
var _react = _interopRequireWildcard(require("react"));
var _material = require("@mui/material");
var _iconsMaterial = require("@mui/icons-material");
var _propTypes = _interopRequireDefault(require("prop-types"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/**
 * DmsCarousel component for displaying slides with navigation, indicators, and autoplay functionality.
 * 
 * Features:
 * - Slide Transition (Swipeable and Auto-playable)
 * - Customizable Indicators (dots, lines, text)
 * - Autoplay with optional interval
 * - Pause on hover
 * - Swipe support for mobile devices
 * 
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The content to be rendered inside the carousel.
 * @param {number} [props.slideInterval=5000] - The interval (in ms) between slides for autoplay.
 * @param {boolean} [props.autoplay=true] - Whether the carousel should autoplay.
 * @param {('dots'|'lines'|'text'|null)} [props.indicatorType=null] - The type of indicator to render ('dots', 'lines', or 'text').
 */
const DmsCarousel = _ref => {
  let {
    children,
    slideInterval = 5000,
    autoplay = true,
    indicatorType = null
  } = _ref;
  const [currentSlideIndex, setCurrentSlideIndex] = (0, _react.useState)(0);
  const [isHovered, setIsHovered] = (0, _react.useState)(false);
  const [touchStart, setTouchStart] = (0, _react.useState)(0);
  const [touchEnd, setTouchEnd] = (0, _react.useState)(0);
  const totalSlides = _react.Children.count(children);
  const goToNextSlide = () => {
    setCurrentSlideIndex(prev => (prev + 1) % totalSlides);
  };
  const goToPreviousSlide = () => {
    setCurrentSlideIndex(prev => (prev - 1 + totalSlides) % totalSlides);
  };

  // Touch handling
  const handleTouchStart = e => setTouchStart(e.touches[0].clientX);
  const handleTouchEnd = e => {
    setTouchEnd(e.changedTouches[0].clientX);
    const diff = touchStart - touchEnd;
    if (diff > 50) goToNextSlide();
    if (diff < -50) goToPreviousSlide();
  };

  // Auto-play
  (0, _react.useEffect)(() => {
    if (autoplay && !isHovered && totalSlides > 1) {
      const timer = setInterval(goToNextSlide, slideInterval);
      return () => clearInterval(timer);
    }
  }, [autoplay, isHovered, slideInterval, currentSlideIndex, totalSlides]);
  return /*#__PURE__*/_react.default.createElement(_material.Box, {
    sx: {
      position: 'relative',
      width: '100%',
      height: '100%',
      overflow: 'hidden'
    },
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
    onTouchStart: handleTouchStart,
    onTouchEnd: handleTouchEnd
  }, /*#__PURE__*/_react.default.createElement(_material.Box, {
    sx: {
      display: 'flex',
      width: "".concat(totalSlides * 100, "%"),
      height: '100%',
      transform: "translateX(-".concat(currentSlideIndex * 100 / totalSlides, "%)"),
      transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
    }
  }, _react.Children.map(children, (child, index) => /*#__PURE__*/_react.default.createElement(_material.Box, {
    key: index,
    sx: {
      width: "".concat(100 / totalSlides, "%"),
      height: '100%',
      flexShrink: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }
  }, child))), /*#__PURE__*/_react.default.createElement(_material.Box, {
    sx: {
      position: 'absolute',
      top: '50%',
      left: 0,
      right: 0,
      display: 'flex',
      justifyContent: 'space-between',
      transform: 'translateY(-50%)',
      px: 2
    }
  }, /*#__PURE__*/_react.default.createElement(_material.Button, {
    onClick: goToPreviousSlide,
    sx: {
      color: 'white',
      bgcolor: 'rgba(0,0,0,0.3)'
    }
  }, /*#__PURE__*/_react.default.createElement(_iconsMaterial.KeyboardArrowLeft, {
    fontSize: "large"
  })), /*#__PURE__*/_react.default.createElement(_material.Button, {
    onClick: goToNextSlide,
    sx: {
      color: 'white',
      bgcolor: 'rgba(0,0,0,0.3)'
    }
  }, /*#__PURE__*/_react.default.createElement(_iconsMaterial.KeyboardArrowRight, {
    fontSize: "large"
  }))), indicatorType && /*#__PURE__*/_react.default.createElement(_material.Box, {
    sx: {
      position: 'absolute',
      bottom: 16,
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      gap: 1
    }
  }, Array.from({
    length: totalSlides
  }).map((_, index) => {
    const isActive = currentSlideIndex === index;
    const indicatorStyles = {
      dots: {
        width: 10,
        height: 10,
        borderRadius: '50%'
      },
      lines: {
        width: 32,
        height: 3,
        borderRadius: '2px'
      },
      text: {
        width: 'auto',
        height: 'auto',
        padding: '0 8px',
        fontSize: '0.875rem'
      }
    };
    return indicatorType === 'text' ? /*#__PURE__*/_react.default.createElement(_material.Box, {
      key: index,
      sx: _objectSpread(_objectSpread({}, indicatorStyles[indicadorType]), {}, {
        cursor: 'pointer',
        color: isActive ? 'primary.main' : 'text.secondary',
        fontWeight: isActive ? 'bold' : 'normal',
        transition: 'all 0.3s ease'
      }),
      onClick: () => setCurrentSlideIndex(index)
    }, isActive ? "".concat(index + 1, " / ").concat(totalSlides) : index + 1) : /*#__PURE__*/_react.default.createElement(_material.Box, {
      key: index,
      sx: _objectSpread(_objectSpread({}, indicatorStyles[indicadorType]), {}, {
        cursor: 'pointer',
        bgcolor: isActive ? 'primary.main' : 'action.disabled',
        transition: 'all 0.3s ease',
        '&:hover': {
          bgcolor: isActive ? 'primary.dark' : 'action.hover'
        }
      }),
      onClick: () => setCurrentSlideIndex(index)
    });
  })));
};

/**
 * PreviousButton component to be used as a custom element in DmsCarousel.
 * 
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The custom previous button content.
 * @returns {JSX.Element} The custom previous button.
 */
exports.DmsCarousel = DmsCarousel;
DmsCarousel.PreviousButton = _ref2 => {
  let {
    children
  } = _ref2;
  return children;
};

/**
 * NextButton component to be used as a custom element in DmsCarousel.
 * 
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The custom next button content.
 * @returns {JSX.Element} The custom next button.
 */
DmsCarousel.NextButton = _ref3 => {
  let {
    children
  } = _ref3;
  return children;
};

/**
 * PropTypes validation for DmsCarousel component.
 */
DmsCarousel.propTypes = {
  children: _propTypes.default.node.isRequired,
  slideInterval: _propTypes.default.number,
  autoplay: _propTypes.default.bool,
  indicatorType: _propTypes.default.oneOf(["dots", "lines", "text"])
};